import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import {
  StyleSheet,
  Animated,
  StatusBar,
} from 'react-native';
import { PanGestureHandler, State, PanGestureHandlerGestureEvent } from 'react-native-gesture-handler';
import { CloseButton } from './CloseButton';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';

// Configuration constants
const DISMISS_THRESHOLD = 150; // Distance to trigger dismiss
const VELOCITY_THRESHOLD = 1000; // Velocity to trigger dismiss
const SNAP_BACK_TENSION = 100;
const SNAP_BACK_FRICTION = 8;

interface DraggableModalProps {
  children: React.ReactNode;
  onClose: () => void;
  statusBarStyle?: 'light-content' | 'dark-content';
  canDrag?: boolean;
  dismissThreshold?: number;
  velocityThreshold?: number;
  testID?: string;
}

export const DraggableModal: React.FC<DraggableModalProps> = ({
  children,
  onClose,
  statusBarStyle = 'light-content',
  canDrag = true,
  dismissThreshold = DISMISS_THRESHOLD,
  velocityThreshold = VELOCITY_THRESHOLD,
  testID,
}) => {
  const translateY = useRef(new Animated.Value(0)).current;
  const lastGestureY = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const panGestureRef = useRef<PanGestureHandler>(null);

  // Reset position when component mounts
  useEffect(() => {
    translateY.setValue(0);
  }, []);

  // Memoized gesture event handler
  const onGestureEvent = useMemo(() => 
    Animated.event(
      [{ nativeEvent: { translationY: translateY } }],
      { 
        useNativeDriver: false,
        listener: (event: PanGestureHandlerGestureEvent) => {
          const { translationY } = event.nativeEvent;
          lastGestureY.current = translationY;
          
          // Only allow downward dragging
          if (translationY < 0) {
            translateY.setValue(0);
          }
        }
      }
    ), [translateY]
  );

  // Memoized state change handler
  const onHandlerStateChange = useCallback((event: PanGestureHandlerGestureEvent) => {
    const { state, translationY, velocityY } = event.nativeEvent;

    switch (state) {
      case State.BEGAN:
        setIsDragging(true);
        break;

      case State.END:
      case State.CANCELLED:
        setIsDragging(false);
        
        // Determine if we should dismiss or snap back
        const shouldDismiss = 
          translationY > dismissThreshold || 
          velocityY > velocityThreshold;

        if (shouldDismiss) {
          // Call onClose immediately to dismiss the entire modal
          onClose();
        } else {
          // Snap back to original position
          Animated.spring(translateY, {
            toValue: 0,
            useNativeDriver: false,
            tension: SNAP_BACK_TENSION,
            friction: SNAP_BACK_FRICTION,
          }).start();
        }
        break;
    }
  }, [dismissThreshold, velocityThreshold, onClose, translateY]);

  // Memoized animated style
  const animatedStyle = useMemo(() => ({
    transform: [{ translateY }],
  }), [translateY]);

  return (
    <Animated.View 
      style={[styles.container, animatedStyle]}
      testID={testID}
      accessibilityRole="none"
      accessibilityLabel="Modal content"
    >
      <StatusBar barStyle={statusBarStyle} />
      <PanGestureHandler
        ref={panGestureRef}
        onGestureEvent={onGestureEvent}
        onHandlerStateChange={onHandlerStateChange}
        activeOffsetY={5}
        failOffsetX={[-30, 30]}
        minPointers={1}
        maxPointers={1}
        shouldCancelWhenOutside={false}
        enabled={canDrag}
      >
        <Animated.View 
          style={styles.gestureArea}
          accessibilityRole="button"
          accessibilityLabel={canDrag ? "Drag to dismiss modal" : "Modal content"}
        >
          {children}
        </Animated.View>
      </PanGestureHandler>
      <CloseButton onPress={onClose} />
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: MARKETPLACE_COLORS.background,
  },
  gestureArea: {
    flex: 1,
  },
});
